import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT } from "../shared"
import { useIsMobile } from "../shared/useIsMobile"
import { useOrgName } from "../shared/BrandLogo"
import { Stat, LevelBadge, TableWrap, Card, Badge, Progress, Btn, Sparkline } from "../shared/components"
import { BarChart, DonutChart } from "../shared/charts"
import { listCourses, getMyProgress, getCourse } from "../api/courses.api"
import { getLearningGain, getTestNotifications, getPendingGrading } from "../api/tests.api"
import { programAnalytics, programCoursesProgress } from "../api/users.api"
import { myCertificates } from "../api/certificates.api"
import { listUpcomingSessions, getRsvp, setRsvp } from "../api/sessions.api"
import {
  getPendingTrainerAssignments,
  acknowledgeTrainerAssignments,
} from "../api/programs.api"
import { AdminDashboard } from "./dashboard/AdminDashboardHome"

// =============================================================
// PARTICIPANT DASHBOARD
// =============================================================
function ParticipantDashboard() {
  const t = useT();
  const { lang, navigate, user, currentProgram, currentProgramId } = useApp();
  const orgName = useOrgName();
  const isMobile = useIsMobile();
  const [courses, setCourses] = React.useState([]);
  const [gain, setGain] = React.useState(null);
  const [upcomingSessions, setUpcomingSessions] = React.useState([]);
  const [notif, setNotif] = React.useState({ posttestOpen: false, pretestPending: false });
  const [progress, setProgress] = React.useState(null);
  const [certs, setCerts] = React.useState([]);
  const [sessionRsvp, setSessionRsvp] = React.useState(null);

  React.useEffect(() => {
    if (!currentProgramId) return;
    listCourses(currentProgramId).then(setCourses).catch(() => setCourses([]));
    getLearningGain(currentProgramId).then(setGain).catch(() => setGain(null));
    listUpcomingSessions(currentProgramId).then(setUpcomingSessions).catch(() => setUpcomingSessions([]));
    getTestNotifications(currentProgramId).then(setNotif).catch(() => setNotif({ posttestOpen: false, pretestPending: false }));
    getMyProgress(currentProgramId).then(setProgress).catch(() => setProgress(null));
    myCertificates().then(setCerts).catch(() => setCerts([]));
  }, [currentProgramId]);

  // Load the participant's RSVP state for the next upcoming session.
  React.useEffect(() => {
    const sess = upcomingSessions[0];
    if (!sess) { setSessionRsvp(null); return; }
    getRsvp(sess.id).then(setSessionRsvp).catch(() => setSessionRsvp(null));
  }, [upcomingSessions]);

  const userName = user?.fullName || "—";
  const firstName = userName.split(" ")[0];
  const main = courses[0];
  // Tanpa program terpilih: nama lembaga, bukan istilah salah satu jenis program.
  const programLabel = currentProgram?.name || orgName;

  // Fetch full course detail (modules/lessons + progressMap) for the "Up next" list.
  const [courseDetail, setCourseDetail] = React.useState(null);
  React.useEffect(() => {
    if (!currentProgramId || !main?.id) { setCourseDetail(null); return; }
    getCourse(currentProgramId, main.id).then(setCourseDetail).catch(() => setCourseDetail(null));
  }, [currentProgramId, main?.id]);

  const upNext = React.useMemo(() => {
    if (!courseDetail) return [];
    const pm = courseDetail.progressMap || {};
    return (courseDetail.modules || [])
      .flatMap((m) => m.lessons)
      .filter((l) => !pm[l.id])
      .slice(0, 3);
  }, [courseDetail]);
  const programLevel = currentProgram?.level || 1;

  const modulePct = progress?.pct != null ? Math.round(progress.pct) : 0;
  const pretestDone = gain?.pretest != null;
  const posttestDone = gain?.posttest != null;
  const programCert = certs.find((c) => c.program_id === currentProgramId) || null;

  const nextSession = upcomingSessions[0];
  const sessionSoon =
    nextSession &&
    new Date(nextSession.scheduled_at) - Date.now() <= 24 * 60 * 60 * 1000;

  const handleRsvp = async (status) => {
    const sess = upcomingSessions[0];
    if (!sess) return;
    try {
      await setRsvp(sess.id, status);
      const fresh = await getRsvp(sess.id);
      setSessionRsvp(fresh);
      toast.success(lang === "id" ? "RSVP tersimpan" : "RSVP saved");
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message);
    }
  };

  return (
    <div className="main-inner">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          {currentProgram && (
            <div className="eyebrow">{(currentProgram.code || '').toUpperCase()} · {currentProgram.name}</div>
          )}
          <h1 className="h1" style={{marginTop: currentProgram ? 6 : 0}}>{t.welcome}, {firstName}.</h1>
          <div className="muted mt-1">
            {lang === "id"
              ? `Terdaftar di ${programLabel}. ${courses.length} kursus aktif.`
              : `Enrolled in ${programLabel}. ${courses.length} active courses.`}
          </div>
        </div>
        <div className="page-header-actions">
          <Btn variant="secondary" icon={<Icon name="check" size={14}/>} onClick={()=>navigate("pretest")}>
            {lang==="id"?"Pretest":"Pretest"}
          </Btn>
          <Btn variant="primary" icon={<Icon name="play" size={14}/>} onClick={() => navigate("courses")}>
            {t.continueLearning}
          </Btn>
        </div>
      </div>

      {/* FR-7.6 — satu ajakan saja, mengikuti urutan pelatihan:
          pretest → materi → posttest.

          Dulu keduanya berdiri sendiri, dan hasilnya terbalik: banner posttest
          muncul begitu gerbangnya terbuka, DAN ajakan pretest ikut disembunyikan
          (`!notif.posttestOpen` ada di syaratnya). Peserta yang baru bergabung
          — nol pretest, nol pelajaran — langsung didorong mengerjakan posttest
          sementara langkah yang benar justru disembunyikan darinya. Teksnya pun
          mengklaim "semua modul selesai" tanpa pernah memeriksa progres, jadi
          klaim itu bohong setiap kali posttest dibuka lewat sakelar
          requires_lessons alih-alih lewat menyelesaikan materi.

          Pretest didahulukan karena ia baseline: mengerjakan posttest lebih
          dulu membuat learning gain tidak bisa dihitung sama sekali. */}
      {notif.pretestPending ? (
        <div className="banner mb-4">
          <Icon name="alert" size={16} color="var(--program-color)"/>
          <div style={{ flex: 1 }} className="small">
            {lang === "id"
              ? "Mulai dari pretest. Skor awal ini yang dipakai menghitung learning gain Anda."
              : "Start with the pretest. This baseline is what your learning gain is measured against."}
          </div>
          <Btn variant="secondary" size="sm" onClick={() => navigate("pretest")}>
            {lang === "id" ? "Mulai pretest" : "Start pretest"}
          </Btn>
        </div>
      ) : notif.posttestOpen && modulePct < 100 ? (
        <div className="banner mb-4">
          <Icon name="book" size={16} color="var(--program-color)"/>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>
              {lang === "id" ? "Lanjutkan materi" : "Continue the materials"}
            </div>
            <div className="small muted mt-1">
              {lang === "id"
                ? `Pretest selesai. Materi baru ${modulePct}% — selesaikan dulu sebelum posttest.`
                : `Pretest done. Materials are ${modulePct}% complete — finish them before the posttest.`}
            </div>
          </div>
          <Btn variant="secondary" size="sm" onClick={() => navigate("courses")}>
            {lang === "id" ? "Buka materi" : "Open materials"}
          </Btn>
        </div>
      ) : notif.posttestOpen ? (
        <div className="banner green mb-4">
          <Icon name="check" size={18} color="var(--success)"/>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>
              {lang === "id" ? "Posttest sudah dibuka" : "Posttest is now open"}
            </div>
            <div className="small muted mt-1">
              {lang === "id"
                ? "Semua modul selesai. Kerjakan posttest untuk menyelesaikan program."
                : "All modules done. Take the posttest to complete the program."}
            </div>
          </div>
          <Btn variant="primary" size="sm" onClick={() => navigate("posttest")}>
            {lang === "id" ? "Kerjakan" : "Take it now"}
          </Btn>
        </div>
      ) : null}
      {sessionSoon && (
        <div className="banner mb-4">
          <Icon name="calendar" size={16} color="var(--program-color)"/>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>
              {lang === "id" ? "Sesi tatap muka dalam 24 jam" : "In-person session within 24 hours"}
            </div>
            <div className="small muted mt-1">
              {nextSession.title} ·{" "}
              {new Date(nextSession.scheduled_at).toLocaleString(
                lang === "id" ? "id-ID" : "en-US",
                { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }
              )}
              {nextSession.location ? ` · ${nextSession.location}` : ""}
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <Stat label={t.activeCourse} value={String(courses.length)} sub={programLabel} />
        <Stat
          label={t.pretest}
          value={gain?.pretest != null ? String(Math.round(gain.pretest)) : "—"}
          sub={lang==="id"?"Skor baseline":"Baseline score"}
        />
        <Stat
          label={t.posttest}
          value={gain?.posttest != null ? String(Math.round(gain.posttest)) : "—"}
          sub={lang==="id"?"Lulus ≥ 70":"Pass ≥ 70"}
        />
        <Stat
          label={t.learningGain}
          value={gain?.gain != null ? `+${Math.round(gain.gain)}` : "—"}
          delta={gain?.gain != null ? String(Math.abs(Math.round(gain.gain))) : undefined}
          deltaDir={gain?.gain >= 0 ? "up" : "down"}
          sub={lang==="id"?"target ≥ 20 poin":"target ≥ 20 pts"}
        />
      </div>

      {/* Two-column main */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr", gap: 16 }}>
        {/* LEFT */}
        <div className="col gap-4">

          {/* Continue learning (prominent first card — matches prototype) */}
          {main ? (
            <Card
              title={t.continueLearning}
              action={
                <Btn variant="ghost" size="sm" icon={<Icon name="arrow_right" size={12}/>} onClick={() => navigate("course", { courseId: main.id })}>
                  {lang==="id"?"Lihat kursus":"View course"}
                </Btn>
              }
            >
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "180px 1fr", gap: 18, alignItems: "center" }}>
                {/* Thumbnail */}
                <div style={{
                  aspectRatio: "16/10",
                  borderRadius: 8,
                  background: `linear-gradient(135deg, var(--program-color-ink), var(--program-color-deep))`,
                  position: "relative", overflow: "hidden",
                }}>
                  <svg viewBox="0 0 180 112" style={{position:"absolute", inset:0, opacity:0.35}}>
                    <path d="M0 80 Q 45 60, 90 80 T 180 80 V112 H0 Z" fill="rgba(255,255,255,0.10)"/>
                    <path d="M0 92 Q 45 76, 90 92 T 180 92 V112 H0 Z" fill="rgba(255,255,255,0.12)"/>
                  </svg>
                  <div style={{position:"absolute", inset:0, display:"grid", placeItems:"center"}}>
                    <div style={{width:40, height:40, borderRadius:"50%", background:"rgba(255,255,255,0.95)", color:"var(--program-color-deep)", display:"grid", placeItems:"center"}}>
                      <Icon name="play" size={16} color="var(--program-color-deep)"/>
                    </div>
                  </div>
                  <div style={{position:"absolute", top:8, left:8}}>
                    <LevelBadge level={programLevel} programColor={currentProgram?.color_theme}/>
                  </div>
                </div>
                {/* Info */}
                <div>
                  <div className="eyebrow">{programLabel}</div>
                  <div className="h2" style={{marginTop:4, lineHeight:1.3}}>{main.title}</div>
                  <div className="muted mt-1" style={{fontSize:13}}>
                    {main.description || (lang === "id"
                      ? `${main._count?.modules ?? 0} modul · lanjutkan belajar`
                      : `${main._count?.modules ?? 0} modules · continue`)}
                  </div>
                  <div style={{ marginTop: 14 }}>
                    <Progress value={modulePct} />
                    <div className="row between mt-2" style={{ fontSize: 12 }}>
                      <span className="muted">
                        {modulePct}% {lang==="id"?"selesai":"complete"}
                      </span>
                      <span className="muted">
                        {progress?.completedLessons ?? 0} / {progress?.totalLessons ?? 0} {lang==="id"?"pelajaran":"lessons"}
                      </span>
                    </div>
                  </div>
                  <div className="row gap-2 mt-4" style={{ flexWrap: "wrap" }}>
                    <Btn variant="primary" size="sm" icon={<Icon name="play" size={13}/>} onClick={() => navigate("course", { courseId: main.id })}>
                      {t.resume}
                    </Btn>
                    <Btn variant="ghost" size="sm" onClick={() => navigate("course", { courseId: main.id })}>
                      {t.syllabus}
                    </Btn>
                    {/* Dokumen referensi berlaku untuk seluruh kursus; tanpa tautan ini peserta
                        harus menebak di mana dokumennya. Jumlahnya dari detail kursus yang
                        sudah dimuat di atas, jadi tanpa permintaan tambahan. */}
                    {(courseDetail?.ref_docs?.length || 0) > 0 && (
                      <Btn variant="ghost" size="sm" icon={<Icon name="doc" size={13}/>}
                        onClick={() => navigate("course", { courseId: main.id, fokus: "dokumen-referensi" })}>
                        {lang==="id"?"Dokumen referensi":"Reference documents"} ({courseDetail.ref_docs.length})
                      </Btn>
                    )}
                  </div>
                </div>
              </div>

              {/* Up next — next incomplete lessons */}
              {upNext.length > 0 && (
                <div style={{ marginTop: 18 }}>
                  <div className="divider" style={{ marginBottom: 12 }} />
                  <div className="eyebrow" style={{ marginBottom: 10 }}>{lang === "id" ? "Selanjutnya" : "Up next"}</div>
                  <div className="col gap-2">
                    {upNext.map((l) => {
                      const cfg = { VIDEO: { i: "play", tone: "warning", id: "VIDEO", en: "VIDEO" }, PDF: { i: "doc", tone: "default", id: "DOK", en: "DOC" }, GAME: { i: "compass", tone: "warning", id: "SIMULASI", en: "GAME" } }[l.type] || { i: "play", tone: "default", id: l.type, en: l.type };
                      return (
                        <div key={l.id} className="row" style={{ padding: "10px 12px", borderRadius: 8, background: "var(--surface-2)", border: "1px solid var(--border-subtle)", gap: 10, cursor: "pointer" }} onClick={() => navigate("course", { courseId: main.id })}>
                          <Icon name={cfg.i} size={14} color="var(--program-color)" />
                          <span style={{ fontWeight: 500, flex: 1, fontSize: 13 }}>{l.title}</span>
                          <Badge tone={cfg.tone}>{lang === "id" ? cfg.id : cfg.en}</Badge>
                          <Icon name="chevron_right" size={14} color="var(--text-3)" />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          ) : (
            <Card>
              <div className="empty">
                <Icon name="book" size={24} color="var(--text-4)"/>
                <div className="muted mt-2">{lang==="id"?"Belum ada kursus.":"No courses yet."}</div>
              </div>
            </Card>
          )}

          {/* Test status (compact — pretest / posttest / learning gain) */}
          <Card title={lang==="id"?"Status Tes & Learning Gain":"Tests & Learning Gain"} action={
            <Btn variant="ghost" size="sm" icon={<Icon name="arrow_right" size={12}/>} onClick={() => navigate("learning-gain")}>
              {lang==="id"?"Detail":"Details"}
            </Btn>
          }>
            <div className="col gap-3">
              {/* Pretest / Posttest status */}
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
                <div>
                  <div className="eyebrow" style={{marginBottom:6}}>{lang==="id"?"Pretest":"Pretest"}</div>
                  {pretestDone ? (
                    <div className="row gap-2">
                      <Badge tone="success" dot>{lang==="id"?"Selesai":"Done"}</Badge>
                      <span className="mono small">{Math.round(gain.pretest)}</span>
                    </div>
                  ) : (
                    <Btn variant="secondary" size="sm" onClick={()=>navigate("pretest")}>
                      {lang==="id"?"Kerjakan":"Take pretest"}
                    </Btn>
                  )}
                </div>
                <div>
                  <div className="eyebrow" style={{marginBottom:6}}>{lang==="id"?"Posttest":"Posttest"}</div>
                  {posttestDone ? (
                    <div className="row gap-2">
                      <Badge tone={gain.posttest >= 70 ? "success" : "warning"} dot>
                        {gain.posttest >= 70 ? (lang==="id"?"Lulus":"Passed") : (lang==="id"?"Belum":"Not passed")}
                      </Badge>
                      <span className="mono small">{Math.round(gain.posttest)}</span>
                    </div>
                  ) : notif.posttestOpen ? (
                    <Btn variant="primary" size="sm" onClick={()=>navigate("posttest")}>
                      {lang==="id"?"Kerjakan":"Take posttest"}
                    </Btn>
                  ) : (
                    <span className="muted" style={{fontSize:12}}>
                      {lang==="id"?"Terbuka setelah 100%":"Unlocks at 100%"}
                    </span>
                  )}
                </div>
              </div>

              {/* Learning gain bars — only when both tests done */}
              {posttestDone && pretestDone && (
                <>
                  <div className="divider"/>
                  <div className="eyebrow" style={{marginBottom:8}}>Learning Gain</div>
                  {[
                    { label: "Pretest", v: Math.round(gain.pretest) },
                    { label: "Posttest", v: Math.round(gain.posttest) },
                  ].map((b) => (
                    <div key={b.label} className="row gap-3 mb-2" style={{alignItems:"center"}}>
                      <span className="mono" style={{width:56, fontSize:11, color:"var(--text-3)"}}>{b.label}</span>
                      <div style={{flex:1, position:"relative"}}>
                        <Progress value={b.v} style={{height:8}} />
                        <div style={{position:"absolute", left:"70%", top:0, bottom:0, width:1, background:"var(--border-strong)"}}/>
                      </div>
                      <span className="mono" style={{width:28, fontSize:12, textAlign:"right"}}>{b.v}</span>
                    </div>
                  ))}
                  <div className="row between" style={{ alignItems: "center" }}>
                    <div className="row gap-2">
                      <Badge tone="success">
                        +{Math.round(gain.gain ?? (gain.posttest - gain.pretest))} pts
                      </Badge>
                      <span className="muted" style={{fontSize:12}}>
                        {lang==="id"?"learning gain":"learning gain"}
                      </span>
                    </div>
                    <Sparkline points={[Math.round(gain.pretest), Math.round(gain.posttest)]} w={120} h={32} />
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Performance charts */}
          <Card eyebrow={lang==="id"?"Analitik":"Analytics"} title={t.chartPerformance}>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "auto 1fr", gap: 20, alignItems: "center" }}>
              <DonutChart value={modulePct} label={t.chartCompletion} color="var(--program-color)" />
              <div>
                <div className="eyebrow" style={{ marginBottom: 10 }}>{t.chartPrePost}</div>
                {(pretestDone || posttestDone) ? (
                  <BarChart
                    height={150}
                    max={100}
                    valueSuffix=""
                    data={[
                      /* Dua kategori, bukan satu hue di dua tingkat terang —
                         yang terakhir itu terbaca sekuensial, seolah posttest
                         lebih tinggi secara konstruksi. */
                      { label: t.chartPretest, value: pretestDone ? Math.round(gain.pretest) : 0, color: "var(--cat-1)" },
                      { label: t.chartPosttest, value: posttestDone ? Math.round(gain.posttest) : 0, color: "var(--cat-2)" },
                    ]}
                  />
                ) : (
                  <div className="muted small" style={{ padding: "24px 0" }}>{t.chartNoData}</div>
                )}
              </div>
            </div>
          </Card>

          {/* "My Courses" list intentionally omitted — Continue Learning above already shows the active course (matches prototype). */}

        </div>

        {/* RIGHT */}
        <div className="col gap-4">

          {/* Upcoming sessions */}
          <Card title={t.upcomingSessions} action={<Badge dot>{lang==="id"?"Terjadwal":"Scheduled"}</Badge>}>
            {upcomingSessions.length === 0 ? (
              <div className="empty small">
                {lang==="id"?"Belum ada sesi terjadwal.":"No sessions scheduled."}
              </div>
            ) : (() => {
              const first = upcomingSessions[0]
              const rest = upcomingSessions.slice(1, 4)
              const dt = new Date(first.scheduled_at)
              const day = dt.toLocaleDateString(lang === "id" ? "id-ID" : "en-US", { day: "numeric" })
              const mon = dt.toLocaleDateString(lang === "id" ? "id-ID" : "en-US", { month: "short" })
              const time = dt.toLocaleTimeString(lang === "id" ? "id-ID" : "en-US", { hour: "2-digit", minute: "2-digit" })
              return (
                <div className="col gap-3">
                  <div className="row gap-3" style={{alignItems:"flex-start"}}>
                    {/* Date badge */}
                    <div style={{
                      width:48, padding:"6px 0", textAlign:"center", flexShrink:0,
                      background:"var(--program-color-soft)", color:"var(--program-color-deep)",
                      borderRadius:"var(--r-md)",
                    }}>
                      <div style={{fontSize:10, textTransform:"uppercase", letterSpacing:".05em", fontWeight:500}}>{mon}</div>
                      <div style={{fontSize:20, fontWeight:600, lineHeight:1.1}}>{day}</div>
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontWeight:500, fontSize:13}}>{first.title}</div>
                      <div className="muted" style={{fontSize:12, marginTop:4}}>
                        <Icon name="clock" size={11}/> {time} · {first.trainer?.full_name || "—"}
                      </div>
                      {first.location && (
                        <div className="muted" style={{fontSize:12, marginTop:2}}>
                          <Icon name="map_pin" size={11}/> {first.location}
                        </div>
                      )}
                    </div>
                  </div>

                  {sessionRsvp && (
                    <div style={{borderTop:"1px solid var(--border-subtle)", paddingTop:12}}>
                      <div className="row between mb-2">
                        <span className="eyebrow">{lang==="id"?"Konfirmasi kehadiran":"RSVP"}</span>
                        <span className="mono muted" style={{fontSize:11}}>
                          {sessionRsvp.going}{sessionRsvp.capacity != null ? ` / ${sessionRsvp.capacity}` : ""} {lang==="id"?"hadir":"going"}
                        </span>
                      </div>
                      <div className="row gap-2">
                        <button
                          className={`btn btn-sm ${sessionRsvp.myStatus === "GOING" ? "btn-primary" : "btn-secondary"}`}
                          style={{flex:1}}
                          onClick={() => handleRsvp("GOING")}
                        >
                          {lang==="id"?"Hadir":"Going"}
                        </button>
                        <button
                          className={`btn btn-sm ${sessionRsvp.myStatus === "NOT_GOING" ? "btn-danger" : "btn-ghost"}`}
                          style={{flex:1}}
                          onClick={() => handleRsvp("NOT_GOING")}
                        >
                          {lang==="id"?"Tidak":"Not going"}
                        </button>
                      </div>
                    </div>
                  )}

                  {rest.length > 0 && (
                    <div style={{borderTop:"1px solid var(--border-subtle)", paddingTop:12}}>
                      <div className="eyebrow mb-2">{lang==="id"?"Selanjutnya":"Coming up"}</div>
                      {rest.map((s) => {
                        const sd = new Date(s.scheduled_at)
                        const when = sd.toLocaleString(lang === "id" ? "id-ID" : "en-US", {
                          weekday:"short", day:"numeric", month:"short", hour:"2-digit", minute:"2-digit",
                        })
                        return (
                          <div key={s.id} className="row gap-2 mb-2">
                            <div style={{width:6, height:6, borderRadius:"50%", background:"var(--amber)", marginTop:5, flexShrink:0}}/>
                            <div style={{flex:1, minWidth:0}}>
                              <div style={{fontSize:12, fontWeight:500}}>{s.title}</div>
                              <div className="muted" style={{fontSize:11}}>{when}</div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })()}
          </Card>

          {/* Certificate status */}
          <Card title={lang==="id"?"Status Sertifikat":"Certificate"} action={<Icon name="cert" size={14} color="var(--text-3)"/>}>
            {/* Syarat kelayakan — HANYA dua, sesuai yang benar-benar ditegakkan
                server (enrollment.service.js): kehadiran ≥ 80% DAN posttest ≥ 70.
                Dulu daftar ini memuat empat baris, menambahkan "Pretest selesai"
                dan "Semua pelajaran" yang bukan syarat sama sekali — peserta
                yang materinya baru separuh bisa mengira dirinya terhalang
                padahal tidak. Keduanya dipindah ke blok Kemajuan di bawah.

                Baris kehadiran juga dulu dipatok mati sebagai "—" karena tidak
                ada endpoint peserta yang mengirim angkanya; sekarang ikut di
                respons learning-gain. */}
            <div className="col gap-2" style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid var(--border-subtle)" }}>
              <div className="tiny muted" style={{ textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>
                {lang==="id"?"Syarat":"Requirements"}
              </div>
              {[
                { ok: posttestDone && gain?.posttest >= 70, label: lang==="id"?"Posttest ≥ 70":"Posttest ≥ 70", val: posttestDone ? String(Math.round(gain.posttest)) : null },
                {
                  ok: gain?.attendancePct != null && gain.attendancePct >= 80,
                  label: lang==="id"?"Kehadiran ≥ 80%":"Attendance ≥ 80%",
                  val: gain?.attendancePct != null ? `${Math.round(gain.attendancePct)}%` : "—",
                },
              ].map((c, i) => (
                <div key={i} className="row between" style={{ fontSize: 13 }}>
                  <span className="row gap-2" style={{ alignItems: "center" }}>
                    <Icon name={c.ok ? "check" : "clock"} size={14} color={c.ok ? "var(--success)" : "var(--text-3)"} />
                    {c.label}
                  </span>
                  {c.ok
                    ? <Badge tone="success" dot>{c.val ?? "OK"}</Badge>
                    : <span className="muted mono" style={{ fontSize: 12 }}>{c.val ?? (lang==="id"?"Belum":"Pending")}</span>}
                </div>
              ))}

              {/* Kemajuan, bukan syarat: pretest dan materi tidak menentukan
                  kelayakan sertifikat. Tetap ditampilkan karena berguna sebagai
                  orientasi, tapi dipisahkan supaya tidak lagi terbaca sebagai
                  penghalang. */}
              <div className="tiny muted" style={{ textTransform: "uppercase", letterSpacing: ".06em", marginTop: 10, marginBottom: 2 }}>
                {lang==="id"?"Kemajuan":"Progress"}
              </div>
              {[
                { ok: pretestDone, label: lang==="id"?"Pretest selesai":"Pretest completed", val: pretestDone ? (lang==="id"?"Selesai":"Done") : null },
                { ok: modulePct >= 100, label: lang==="id"?"Semua pelajaran":"All lessons", val: `${modulePct}%` },
              ].map((c, i) => (
                <div key={`p${i}`} className="row between" style={{ fontSize: 13 }}>
                  <span className="row gap-2" style={{ alignItems: "center" }}>
                    <Icon name={c.ok ? "check" : "clock"} size={14} color={c.ok ? "var(--success)" : "var(--text-3)"} />
                    <span className="muted">{c.label}</span>
                  </span>
                  <span className="muted mono" style={{ fontSize: 12 }}>{c.val ?? (lang==="id"?"Belum":"Pending")}</span>
                </div>
              ))}
            </div>
            {programCert ? (
              programCert.claimed_at ? (
                <div className="col gap-3">
                  <Badge tone="success" dot>{lang==="id"?"Sudah diklaim":"Claimed"}</Badge>
                  <div className="mono" style={{fontSize:12}}>{programCert.cert_no}</div>
                  {programCert.pdf_url && (
                    <a className="btn-secondary btn-sm" href={programCert.pdf_url} target="_blank" rel="noopener noreferrer" style={{display:"inline-flex",alignItems:"center",gap:6,padding:"4px 9px",fontSize:12,fontWeight:500,border:"1px solid var(--border)",borderRadius:"var(--r-md)",background:"var(--surface)",color:"var(--text)",textDecoration:"none"}}>
                      <Icon name="download" size={13}/>{lang==="id"?"Unduh sertifikat":"Download"}
                    </a>
                  )}
                </div>
              ) : (
                <div className="col gap-3">
                  <Badge tone="brand" dot>{lang==="id"?"Kode terkirim":"Code sent"}</Badge>
                  <div className="muted" style={{fontSize:12}}>
                    {lang==="id" ? "Cek email, lalu klaim sertifikat." : "Check email, then claim your certificate."}
                  </div>
                  <Btn variant="primary" size="sm" block onClick={()=>navigate("claim")}>
                    {lang==="id"?"Klaim sertifikat":"Claim certificate"}
                  </Btn>
                </div>
              )
            ) : (
              <div className="col gap-3">
                <Badge>{lang==="id"?"Belum tersedia":"Not available yet"}</Badge>
                <div className="muted" style={{fontSize:12, lineHeight:1.5}}>
                  {lang==="id"
                    ? "Lulus posttest (≥70) dan kehadiran ≥80%. Materi tidak menentukan kelayakan."
                    : "Pass the posttest (≥70) and reach ≥80% attendance. Materials don't affect eligibility."}
                </div>
              </div>
            )}
          </Card>

          {/* Ask AI */}
          <Card eyebrow="AI · Knowledge Base" title={String(t.aiAssistant)}>
            <div className="col gap-3">
              <div style={{
                padding:"12px 14px",
                background:"var(--surface-2)",
                borderRadius:"var(--r-md)",
                border:"1px solid var(--border-subtle)",
                fontSize:13,
              }}>
                <div style={{color:"var(--text-3)", fontSize:11, marginBottom:6}} className="eyebrow">
                  {lang==="id"?"Contoh pertanyaan":"Sample Q&A"}
                </div>
                {/* Contoh umum: asisten menjawab dari dokumen Knowledge Base lembaga ini,
                    jadi topiknya tidak boleh mengandaikan satu jenis program. Dulu di sini
                    tertulis pertanyaan maritim dengan sumber rekaan ("IMO OPRC Manual
                    §4.2") yang tampil di setiap lembaga. */}
                {lang==="id"
                  ? "Apa saja poin utama dari materi modul pertama?"
                  : "What are the key points of the first module?"}
                <div className="muted mt-2" style={{fontSize:11}}>
                  {lang==="id"
                    ? "Jawaban disertai sumber dari dokumen Knowledge Base."
                    : "Answers cite their source from the Knowledge Base documents."}
                </div>
              </div>
              <Btn variant="primary" block icon={<Icon name="ai" size={14}/>} onClick={() => navigate("ai")}>
                {t.askAnything}
              </Btn>
            </div>
          </Card>

        </div>
      </div>
    </div>
  );
}

// =============================================================
// TRAINER DASHBOARD
// =============================================================
function TrainerDashboard() {
  const t = useT();
  const { lang, navigate, user, currentProgramId, currentProgram, currentClassId } = useApp();
  const orgName = useOrgName();
  const isMobile = useIsMobile();
  const [analytics, setAnalytics] = React.useState(null);
  const [coursesProgress, setCoursesProgress] = React.useState([]);
  const [pendingGrading, setPendingGrading] = React.useState([]);
  const [expanded, setExpanded] = React.useState(null); // courseId currently expanded
  const [pendingAssignments, setPendingAssignments] = React.useState([]);

  React.useEffect(() => {
    if (!currentProgramId) return;
    programAnalytics(currentProgramId).then(setAnalytics).catch(() => setAnalytics(null));
    programCoursesProgress(currentProgramId).then(setCoursesProgress).catch(() => setCoursesProgress([]));
    getPendingGrading(currentProgramId, currentClassId).then(setPendingGrading).catch(() => setPendingGrading([]));
  }, [currentProgramId, currentClassId]);

  // FR — trainer assignment notification banner
  React.useEffect(() => {
    getPendingTrainerAssignments()
      .then(setPendingAssignments)
      .catch(() => setPendingAssignments([]));
  }, []);

  const handleAckAssignments = async () => {
    try {
      await acknowledgeTrainerAssignments();
      setPendingAssignments([]);
    } catch {
      setPendingAssignments([]);
    }
  };

  const userName = user?.fullName || "—";
  const programLabel = currentProgram?.name || orgName;

  return (
    <div className="main-inner">
      <div className="page-header">
        <div className="page-header-text">
          {currentProgram && <div className="eyebrow">{(currentProgram.code || '').toUpperCase()} · {currentProgram.name}</div>}
          <h1 className="h1" style={{marginTop: currentProgram ? 6 : 0}}>{t.welcome}, {userName}.</h1>
          <div className="muted mt-1">
            {analytics?.total ?? 0} {lang==="id"?"peserta":"participants"} · {coursesProgress.length} {lang==="id"?"kursus":"courses"} · {programLabel}
          </div>
        </div>
        <div className="page-header-actions">
          <Btn variant="secondary" icon={<Icon name="upload" size={14}/>} onClick={() => navigate("kb")}>
            {lang==="id"?"Dokumen":"Docs"}
          </Btn>
          <Btn variant="primary" icon={<Icon name="upload" size={14}/>} onClick={() => navigate("videos")}>
            {lang==="id"?"Upload Video":"Upload Video"}
          </Btn>
        </div>
      </div>

      {/* Trainer assignment banner */}
      {pendingAssignments.length > 0 && (
        <div className="banner mb-4">
          <Icon name="bell" size={16} color="var(--program-color)"/>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>
              {lang === "id" ? "Anda di-assign sebagai trainer" : "You've been assigned as trainer"}
            </div>
            <div className="small muted mt-1">
              {(lang === "id" ? "Program: " : "Programs: ") +
                pendingAssignments.map((a) => a.program?.name).filter(Boolean).join(", ")}
            </div>
          </div>
          <Btn variant="secondary" size="sm" onClick={handleAckAssignments}>
            {lang === "id" ? "Mengerti" : "Got it"}
          </Btn>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        <Stat label={t.activeParticipants} value={String(analytics?.total ?? 0)} sub={programLabel}/>
        <Stat label={lang==="id"?"Rata-rata Pretest":"Avg Pretest"} value={analytics?.avgPretest != null ? String(Math.round(analytics.avgPretest)) : "—"}/>
        <Stat
          label={lang==="id"?"Rata-rata Posttest":"Avg Posttest"}
          value={analytics?.avgPosttest != null ? String(Math.round(analytics.avgPosttest)) : "—"}
          delta={analytics?.avgGain != null ? `+${Math.round(analytics.avgGain)}` : undefined}
          deltaDir={analytics?.avgGain > 0 ? "up" : undefined}
          sub="learning gain"
        />
        <Stat
          label={lang==="id"?"Memenuhi syarat":"Cert-eligible"}
          value={String(analytics?.eligible ?? 0)}
          sub={analytics?.total ? `${Math.round(((analytics.eligible || 0) / analytics.total) * 100)}% of total` : ""}
        />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1.4fr 1fr", gap: 16 }}>
        <div className="col gap-4">
          {/* Charts */}
          <Card eyebrow={lang==="id"?"Analitik":"Analytics"} title={t.chartPerformance}>
            <div className="col gap-4">
              <div>
                <div className="eyebrow" style={{ marginBottom: 10 }}>{t.chartPerCourse}</div>
                {coursesProgress.length ? (
                  <BarChart
                    height={170}
                    max={100}
                    valueSuffix="%"
                    data={coursesProgress.slice(0, 6).map((c) => ({ label: c.title, value: Math.round(c.avgCompletionPct) }))}
                  />
                ) : (
                  <div className="muted small" style={{ padding: "16px 0" }}>{t.chartNoData}</div>
                )}
              </div>
              {(analytics?.avgPretest != null || analytics?.avgPosttest != null) && (
                <div style={{ borderTop: "1px solid var(--border-subtle)", paddingTop: 16 }}>
                  <div className="eyebrow" style={{ marginBottom: 10 }}>{t.chartPrePost}</div>
                  <BarChart
                    height={150}
                    max={100}
                    data={[
                      { label: t.chartPretest, value: Math.round(analytics?.avgPretest || 0), color: "var(--cat-1)" },
                      { label: t.chartPosttest, value: Math.round(analytics?.avgPosttest || 0), color: "var(--cat-2)" },
                    ]}
                  />
                </div>
              )}
            </div>
          </Card>

          {/* Per-course progress */}
          <Card
            title={lang==="id"?"Progres Peserta per Kursus":"Participant Progress per Course"}
            action={<span className="muted" style={{fontSize:12}}>{coursesProgress.length} {lang==="id"?"kursus":"courses"}</span>}
            noPad
          >
            {coursesProgress.length === 0 ? (
              <div className="card-body"><div className="muted small" style={{textAlign:"center"}}>{lang==="id"?"Belum ada kursus.":"No courses yet."}</div></div>
            ) : coursesProgress.map((c) => {
              const isOpen = expanded === c.courseId;
              return (
                <div key={c.courseId} style={{borderTop:"1px solid var(--border-subtle)"}}>
                  <div
                    className="row"
                    style={{padding:"14px 18px", gap:12, cursor:"pointer"}}
                    onClick={() => setExpanded(isOpen ? null : c.courseId)}
                  >
                    <Icon name={isOpen ? "chevron_down" : "chevron_right"} size={14} color="var(--text-4)"/>
                    <div className="col" style={{flex:1, minWidth:0}}>
                      <span style={{fontWeight:500, fontSize:13}}>{c.title}</span>
                      <span className="muted" style={{fontSize:11, marginTop:2}}>
                        {c.lessonCount} {lang==="id"?"pelajaran":"lessons"} · {c.totalParticipants} {lang==="id"?"peserta":"participants"}
                      </span>
                    </div>
                    <div style={{width:140}}>
                      <div className="row between mb-1">
                        <span className="muted" style={{fontSize:11}}>{lang==="id"?"Avg selesai":"Avg done"}</span>
                        <span className="mono" style={{fontSize:11}}>{c.avgCompletionPct}%</span>
                      </div>
                      <Progress value={c.avgCompletionPct} />
                    </div>
                  </div>
                  {isOpen && (
                    <div style={{background:"var(--surface-2)", padding:"4px 0", borderTop:"1px solid var(--border-subtle)"}}>
                      {c.participants.length === 0 ? (
                        <div className="muted small" style={{textAlign:"center", padding:12}}>
                          {lang==="id"?"Belum ada peserta.":"No participants."}
                        </div>
                      ) : (
                        <TableWrap>
                          <table className="tbl">
                            <thead><tr>
                              <th>{lang==="id"?"Peserta":"Participant"}</th>
                              <th>{lang==="id"?"Pelajaran":"Lessons"}</th>
                              <th>%</th>
                              <th>Posttest</th>
                            </tr></thead>
                            <tbody>
                              {c.participants.map((p) => (
                                <tr key={p.userId}>
                                  <td>
                                    <div style={{fontWeight:500, fontSize:12}}>{p.name}</div>
                                    <div className="muted" style={{fontSize:11}}>{p.email}</div>
                                  </td>
                                  <td className="mono" style={{fontSize:12}}>{p.completedLessons} / {c.lessonCount}</td>
                                  <td className="mono" style={{fontSize:12}}>{Math.round(p.completionPct)}%</td>
                                  <td className="mono" style={{fontSize:12}}>{p.posttestScore != null ? Math.round(p.posttestScore) : "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </TableWrap>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </Card>
        </div>

        <div className="col gap-4">
          {/* Pending grading */}
          <Card
            title={lang==="id"?"Menunggu Penilaian":"Pending Grading"}
            action={<Badge tone={pendingGrading.length > 0 ? "warning" : "default"}>{pendingGrading.length}</Badge>}
          >
            {pendingGrading.length === 0 ? (
              <div className="empty small">
                {lang==="id"?"Tidak ada esai menunggu nilai.":"No essays awaiting grading."}
              </div>
            ) : pendingGrading.map((a, i) => (
              <div
                key={a.id}
                className="row gap-3"
                style={{padding: i>0 ? "10px 0 0" : "0", cursor:"pointer"}}
                onClick={() => navigate("learning-gain")}
              >
                <div style={{width:6, height:6, borderRadius:"50%", background:"var(--amber)", marginTop:4, flexShrink:0}}/>
                <div style={{flex:1, minWidth:0}}>
                  <div style={{fontWeight:500, fontSize:13}}>{a.user?.full_name || "—"}</div>
                  <div className="muted" style={{fontSize:11}}>
                    {a.test?.type === "PRETEST" ? "Pretest" : "Posttest"}
                    {a.submitted_at ? ` · ${new Date(a.submitted_at).toLocaleDateString(lang==="id"?"id-ID":"en-US")}` : ""}
                  </div>
                </div>
                <Icon name="chevron_right" size={12} color="var(--text-4)"/>
              </div>
            ))}
          </Card>

          {/* Upload shortcuts */}
          <Card title={lang==="id"?"Materi Pelatihan":"Training Materials"}>
            <div className="col gap-2">
              <Btn variant="primary" block style={{justifyContent:"flex-start"}} icon={<Icon name="upload" size={13}/>} onClick={() => navigate("videos")}>
                <span style={{flex:1, textAlign:"left"}}>{lang==="id"?"Upload Video":"Upload Video"}</span>
                <Icon name="chevron_right" size={12}/>
              </Btn>
              <Btn variant="secondary" block style={{justifyContent:"flex-start"}} icon={<Icon name="upload" size={13}/>} onClick={() => navigate("kb")}>
                <span style={{flex:1, textAlign:"left"}}>{lang==="id"?"Upload Dokumen":"Upload Document"}</span>
                <Icon name="chevron_right" size={12}/>
              </Btn>
              <div className="muted" style={{fontSize:11}}>
                {lang==="id"
                  ? "Video VOD untuk peserta · PDF/DOCX untuk Knowledge Base AI"
                  : "VOD for participants · PDF/DOCX for AI Knowledge Base"}
              </div>
            </div>
          </Card>

          {/* Quick actions */}
          <Card title={lang==="id"?"Aksi Cepat":"Quick Actions"}>
            <div className="col gap-1">
              {[
                {i:"edit",  l: lang==="id"?"Editor Tes":"Test Editor",         to:"test-editor"},
                {i:"ai",    l: lang==="id"?"Asisten AI":"AI Assistant",         to:"ai"},
                {i:"chart", l: lang==="id"?"Learning Gain":"Learning Gain",     to:"learning-gain"},
                {i:"chart", l: lang==="id"?"Analitik Progres":"Progress Analytics", to:"progress"},
              ].map((q,i) => (
                <Btn key={i} variant="ghost" block style={{justifyContent:"flex-start", height:34, padding:"0 6px"}} icon={<Icon name={q.i} size={14}/>} onClick={() => navigate(q.to)}>
                  <span style={{flex:1, textAlign:"left", fontSize:13}}>{q.l}</span>
                  <Icon name="chevron_right" size={12} color="var(--text-4)"/>
                </Btn>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

export { ParticipantDashboard, TrainerDashboard, AdminDashboard }
